import { useEffect, useRef, useState, useMemo, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentSection } from './ContentSection'
import { RichContent } from './RichContent'
import { ContextBlock } from './ContextBlock'
import { BookmarkIconButton } from './BookmarkButton'
import { ContentZoomControls } from './ContentZoomControls'
import { EditThisPageButton } from './EditThisPageButton'
import { NotePanel } from './NotePanel'
import { useIsExpanded } from '@/hooks/useLayoutMode'
import { NotesFab } from './NotesFab'
import { useAppStore } from '@/store/appStore'
import { useContentScrollArea } from '@/hooks/useContentScrollArea'
import { useOfflineMediaUrl } from '@/hooks/useOfflineMediaUrl'
import { toAbsoluteMediaUrls } from '@/utils/mediaReferences'
import type { ApiPracticeArea, ApiContentTag, GetContentBundleResponse } from '@/types/api'
import { LevelType } from '@/types/api'
import {ContentGroup} from "@/components/content/ContentGroup.tsx"
import { runtimeEnv } from '@/utils/runtimeEnv';
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'
import { useFilterChips } from '@/hooks/useFilterChips'
import { FilterChips } from './FilterChips'
import { mergeChips } from '@/utils/filterChips'
import { groupPracticesByPrimaryLevel } from '@/utils/contentOrder'

interface PracticeAreaContentProps {
  practiceArea: ApiPracticeArea
  bundle: GetContentBundleResponse
  highlightTerm?: string
  scrollToCcId?: number
}

export function PracticeAreaContent({ practiceArea, bundle, highlightTerm, scrollToCcId }: PracticeAreaContentProps) {
  const { t } = useTranslation()
  const navigate = useGuardedNavigate()
  const { selectedContentTagIds, selectedLevelId, levelFilterExact, selectedCustomViewId, customViews } = useAppStore()
  const [noteOpen, setNoteOpen] = useState(false)
  const chipsByPractice = useFilterChips(bundle)
  // Docked vs popped-out is a persisted user preference, not per-page state.
  // The floating note window needs a cursor to drag and a `resize` handle to size,
  // and it would cover the content it annotates on a phone. Below lg the panel is
  // always docked. The store value is read but never written: a desktop preference
  // must survive a visit on a small screen.
  const notePoppedPreference = useAppStore((s) => s.notesPoppedOut)
  const contentZoom = useAppStore((s) => s.contentZoom)
  const isExpandedLayout = useIsExpanded()
  const notePopped = notePoppedPreference && isExpandedLayout
  const setNotePopped = useAppStore((s) => s.setNotesPoppedOut)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  // Give the scroll area focus on navigation so arrow / page keys scroll the content
  useContentScrollArea(scrollAreaRef, practiceArea.id)

  // Shared Set so each glossary term is only highlighted on its first occurrence
  // across all sections of this page (intent, value, required, explanatory, context blocks).
  // Reset whenever the user navigates to a different practice area.
  const seenGlossaryTermIds = useRef(new Set<number>())
  useEffect(() => {
    seenGlossaryTermIds.current = new Set()
  }, [practiceArea.id])

  useEffect(() => {
    if (!scrollAreaRef.current) return
    if (highlightTerm && !scrollToCcId) {
      const firstMark = scrollAreaRef.current.querySelector('mark')
      firstMark?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else if (!highlightTerm && !scrollToCcId) {
      scrollAreaRef.current.scrollTop = 0
    }
  }, [highlightTerm, scrollToCcId])

  useEffect(() => {
    if (!scrollToCcId) return
    const tryScroll = (attemptsLeft: number) => {
      const el = document.getElementById(`cc-${scrollToCcId}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add('context-block-highlight')
        const timer = setTimeout(() => el.classList.remove('context-block-highlight'), 3000)
        return () => clearTimeout(timer)
      } else if (attemptsLeft > 0) {
        setTimeout(() => tryScroll(attemptsLeft - 1), 150)
      }
    }
    tryScroll(10)
  }, [scrollToCcId])

  const tagMap = bundle.contentTags.reduce<Record<number, ApiContentTag>>((acc, t) => {
    acc[t.id] = t
    return acc
  }, {})

   // Find contextual content linked directly to this practice area,
   // filtered by the selected context tags if any are active.
   // Sort by the content tag's sortOrder to match the filter menu order.
   const contextualItems = bundle.contextualContent
     .filter((cc) => {
       const linkedToThis = cc.practiceAreaId === practiceArea.id
       if (!linkedToThis) return false
       // If -1 (all) is selected, don't filter (show all contexts). Otherwise, filter by selected tag IDs.
       if (selectedContentTagIds.length > 0 && !selectedContentTagIds.includes(-1) && !selectedContentTagIds.includes(cc.contentTagId)) return false
       return true
     })
     .sort((a, b) => {
       const tagA = tagMap[a.contentTagId]
       const tagB = tagMap[b.contentTagId]
       // Sort by content tag sortOrder (to match filter menu order)
       return (tagA?.sortOrder ?? 0) - (tagB?.sortOrder ?? 0)
     })

  const selectedLevel = bundle.levels.find((l) => l.id === selectedLevelId) ?? null
  const isMddapLevel = selectedLevel?.category === LevelType.Mddap

  const activeCustomView = selectedCustomViewId !== null
    ? customViews.find((cv) => cv.id === selectedCustomViewId) ?? null
    : null

  const cvEntry = activeCustomView
    ? activeCustomView.practiceAreas.find((cvpa) => cvpa.practiceAreaId === practiceArea.id) ?? null
    : null
  const cvLevelId = cvEntry?.levelId ?? null

  const effectiveLevelId = (() => {
    if (!activeCustomView) return selectedLevelId
    if (cvLevelId === null) return selectedLevelId
    if (selectedLevelId === null) return cvLevelId
    const cvSeq = bundle.levels.find((l) => l.id === cvLevelId)?.sequence ?? 0
    const selSeq = bundle.levels.find((l) => l.id === selectedLevelId)?.sequence ?? 0
    return cvSeq <= selSeq ? cvLevelId : selectedLevelId
  })()

  const eligibleLevelIds: Set<number> | null = (() => {
    if (isMddapLevel && selectedLevelId !== null) {
      return new Set([selectedLevelId])
    }
    if (effectiveLevelId === null) return null
    if (levelFilterExact) {
      return new Set([effectiveLevelId])
    }
    const effectiveLevel = bundle.levels.find((l) => l.id === effectiveLevelId)
    const effectiveSequence = effectiveLevel?.sequence ?? 0
    return new Set(bundle.levels.filter((l) => l.sequence <= effectiveSequence).map((l) => l.id))
  })()

  const practices = bundle.practices
    .filter((p) => {
      if (p.practiceAreaId !== practiceArea.id) return false
      if (eligibleLevelIds === null) return true
      if (isMddapLevel && selectedLevelId !== null) {
        return bundle.practiceLevels.some((pl) => pl.practiceId === p.id && pl.levelId === selectedLevelId)
      }
      const levelIdsForPractice = bundle.practiceLevels
        .filter((pl) => pl.practiceId === p.id)
        .map((pl) => pl.levelId)
      if (levelIdsForPractice.length === 0) return p.isDraft
      return levelIdsForPractice.some((lid) => eligibleLevelIds.has(lid))
    })
    .sort((a, b) => a.sortOrder - b.sortOrder)

  const practiceAreaChips = chipsByPractice ? mergeChips(practices.map((p) => chipsByPractice.get(p.id))) : undefined

  // Collect related practice areas for this PA (bi-directional)
  const practiceAreaMap = bundle.practiceAreas.reduce<Record<number, ApiPracticeArea>>((acc, pa) => {
    acc[pa.id] = pa
    return acc
  }, {})

  const relatedPracticeAreas = bundle.relatedPracticeAreas
    .filter((r) => r.sourcePracticeAreaId === practiceArea.id)
    .map((r) => practiceAreaMap[r.targetPracticeAreaId])
    .filter((pa): pa is ApiPracticeArea => pa !== undefined)
    .sort((a, b) => a.sortOrder - b.sortOrder)

  // Resolve icon URI to an absolute URL, then resolve to a blob: URL when offline.
  const rawIconUrl = useMemo(
    () => (practiceArea.iconUri ? toAbsoluteMediaUrls(practiceArea.iconUri, runtimeEnv.VITE_API_BASE_URL || '') : null),
    [practiceArea.iconUri],
  )

  const iconUrl = useOfflineMediaUrl(rawIconUrl)

  return (
    <div className={`pa-content rich-tables content-with-note-panel${noteOpen && !notePopped ? ' note-panel-open' : ''}`}>
      {/* Header */}
      <div className="pa-header">
        <div className="pa-icon" aria-hidden="true">
          {iconUrl ? (
            <img src={iconUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            practiceArea.acronym
          )}
        </div>
        <div className="practice-header-title">
          <h1 className="pa-name">{practiceArea.displayName}</h1>
          <FilterChips chips={practiceAreaChips} variant="full" className="filter-chips--header" />
        </div>
        <BookmarkIconButton contentType="PracticeArea" entityId={practiceArea.id} />
        <EditThisPageButton adminPath={`/admin/practice-areas/${practiceArea.id}`} />
      </div>

      {/* Scrollable content area */}
      <div className="content-scroll-frame">
        <ContentZoomControls />
        <div className="content-scroll-area content-scroll-area--zoomable" ref={scrollAreaRef} tabIndex={0} style={{ '--content-zoom': contentZoom } as CSSProperties}>
          <ContentGroup heading={t('content.requiredPaInformation')}>
            {/* Intent */}
            <ContentSection
              id={`pa-${practiceArea.id}-intent`}
              heading={t('content.intent')}
            >
              <RichContent html={practiceArea.intent ?? ''} original={practiceArea.originalFields?.intent} bundle={bundle} enableGlossary seenTermIds={seenGlossaryTermIds.current} highlightTerm={highlightTerm} />
            </ContentSection>
    
            {/* Value */}
            <ContentSection
              id={`pa-${practiceArea.id}-value`}
              heading={t('content.value')}
            >
              <RichContent html={practiceArea.value ?? ''} original={practiceArea.originalFields?.value} bundle={bundle} enableGlossary seenTermIds={seenGlossaryTermIds.current} highlightTerm={highlightTerm} />
            </ContentSection>
    
            {/* Additional Required */}
            <ContentSection
              id={`pa-${practiceArea.id}-required`}
              heading={t('content.additionalRequired')}
            >
              {practiceArea.additionalRequiredInformation ? (
                <RichContent html={practiceArea.additionalRequiredInformation} original={practiceArea.originalFields?.additionalRequiredInformation} bundle={bundle} enableGlossary seenTermIds={seenGlossaryTermIds.current} highlightTerm={highlightTerm} />
              ) : (
                <p className="empty-section">{t('content.emptyContent')}</p>
              )}
            </ContentSection>
          </ContentGroup>

          <ContentGroup heading={t('content.explanatoryPaInformation')}>
            {/* Explanatory + Practice Summary */}
            <ContentSection
             id={`pa-${practiceArea.id}-practicesummary`}
             heading={t('content.practiceSummary')}
             collapsible
            >
             <div className="practice-summary">
               <div className="practice-summary-by-level">
                 {groupPracticesByPrimaryLevel(practices, bundle.practiceLevels, bundle.levels)
                   .filter((group) => group.level !== null)
                   .map(({ level, practices: levelPractices }) => {
                     return (
                       <div key={level!.id} className="level-group">
                         <div className="level-heading">{level!.displayName}</div>
                         {levelPractices.map((p) => {
                           const chips = chipsByPractice?.get(p.id)
                           const descriptionId = chips?.length ? `practice-filter-chips-${p.id}` : undefined
                           return (
                           <div key={p.id} className="practice-item">
                             <a
                               href={`/practice/${p.id}`}
                               aria-describedby={descriptionId}
                               onClick={(e) => {
                                 e.preventDefault()
                                 navigate(`/practice/${p.id}`)
                               }}
                               className="practice-name-link"
                             >
                               {p.displayName}
                             </a>
                             {p.statement && (
                               <div className="practice-statement">
                                 - {p.statement}
                               </div>
                             )}
                             <FilterChips chips={chips} tooltips descriptionId={descriptionId} />
                           </div>
                           )
                         })}
                       </div>
                     )
                   })}
               </div>
             </div>
            </ContentSection>
            {practiceArea.additionalExplanatoryInformation && (
              <ContentSection
                id={`pa-${practiceArea.id}-explanatory`}
                heading={t('content.additionalExplanatory')}
                collapsible
              >
                <RichContent html={practiceArea.additionalExplanatoryInformation} original={practiceArea.originalFields?.additionalExplanatoryInformation} bundle={bundle} enableGlossary seenTermIds={seenGlossaryTermIds.current} highlightTerm={highlightTerm} />
              </ContentSection>
            )}
          </ContentGroup>

          {/* Related Practice Areas */}
          {relatedPracticeAreas.length > 0 && (
            <ContentSection
              id={`pa-${practiceArea.id}-related`}
              heading={t('content.relatedPracticeAreas')}
            >
              <div className="related-practice-areas-list">
                {relatedPracticeAreas.map((rpa) => (
                  <div key={rpa.id} className="related-practice-area-item">
                    <a
                      href={`/practice-area/${rpa.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        navigate(`/practice-area/${rpa.id}`)
                      }}
                      className="practice-name-link"
                    >
                      <span className="related-practice-area-acronym">{rpa.acronym}</span>
                      {' \u2013 '}
                      <span className="related-practice-area-name">{rpa.displayName}</span>
                    </a>
                  </div>
                ))}
              </div>
            </ContentSection>
          )}

          {/* Context-Specific Content blocks */}
          {contextualItems.length > 0 && (
            <div className="context-blocks">
              <h2 className="context-blocks-heading">{t('content.contextSpecific')}</h2>
              {contextualItems.map((cc) => (
                <ContextBlock
                  key={cc.id}
                  contextualContent={cc}
                  tag={tagMap[cc.contentTagId]}
                  bundle={bundle}
                  enableGlossary
                  seenTermIds={seenGlossaryTermIds.current}
                  highlightTerm={highlightTerm}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Note flyout panel */}
      {noteOpen && (
        <NotePanel
          contentType="PracticeArea"
          entityId={practiceArea.id}
          popped={notePopped}
          onPoppedChange={setNotePopped}
          onClose={() => setNoteOpen(false)}
        />
      )}

      {/* Floating action button. While the note is open it is rendered by
          NotePanel instead, anchored to the panel's top edge; the popped-out
          window has its own controls and needs no button at all. */}
      {!noteOpen && (
        <NotesFab
          contentType="PracticeArea"
          entityId={practiceArea.id}
          noteOpen={false}
          onToggle={() => setNoteOpen(true)}
        />
      )}
    </div>
  )
}
